import React from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './SettingsSection.styles';
import { ROUTES } from '@/constants/routes';
import {
  EmailStatusConfirmedIcon,
  EmailStatusWarningIcon,
} from '@/components/UI/Icons/Icons';
import { CustomLink } from '@/components/CustomLink/CustomLink';
import { Input } from '@/components/UI/Input/Input';
import { EmailConfirmationModal } from '@/features/Profile/sections/ProfileSettings/components/EmailConfirmationModal';
import { RemoveAccountModalSheet } from '@/features/Profile/sections/ProfileSettings/components/RemoveAccountModalSheet';
import { WarningModal } from '@/components/Ad/WarningModal';
import { Button } from '@/components/UI/Button/Button';
import { useSettingsSection } from '@/features/Profile/sections/ProfileSettings/hooks';

function noop() {
  return undefined;
}

export const SettingsSection = () => {
  const { t } = useTranslation();
  const {
    user,
    phone,
    registrationDate,
    destroyAccountError,
    hasEmailStatusIcon,
    isDestroyingUser,
    isEmailConfirmationOpen,
    isEmailConfirmationVisible,
    isEmailConfirmed,
    isRemoveAccountOpen,
    isRemoveAccountConfirmOpen,
    isRemoveAccountErrorOpen,
    isRemoveAccountVisible,
    openEmailConfirmation,
    closeEmailConfirmation,
    openRemoveAccount,
    closeRemoveAccount,
    openRemoveAccountConfirm,
    closeRemoveAccountConfirm,
    closeRemoveAccountError,
    removeAccount,
  } = useSettingsSection();

  return (
    <S.Container>
      <S.ProfileCard>
        <S.Header>
          <S.MainTitle>{t('pages.cabinet.profile')}</S.MainTitle>
          <S.EditLink forwardedAs={CustomLink} href={ROUTES.editProfile}>
            {t('pages.cabinet.edit_data')}
          </S.EditLink>
        </S.Header>

        <S.Fields>
          <Input
            appearance="filled"
            disabled
            label={t('common.name')}
            onChange={noop}
            value={user.name}
            withEllipsis
          />

          <S.EmailInputContainer>
            <S.FieldWithIcon>
              <Input
                appearance="filled"
                disabled
                hasAdditionalPadding={hasEmailStatusIcon}
                label="E-mail"
                onChange={noop}
                value={user.email}
                withEllipsis
              />
              {isEmailConfirmationVisible && (
                <S.FieldIcon>
                  <EmailStatusWarningIcon width={20} height={20} />
                </S.FieldIcon>
              )}
              {isEmailConfirmed && (
                <S.FieldIcon>
                  <EmailStatusConfirmedIcon width={17} height={10} />
                </S.FieldIcon>
              )}
            </S.FieldWithIcon>

            {isEmailConfirmationVisible && (
              <S.EmailWarning>
                <S.EmailWarningText>
                  {t('pages.cabinet.email_confirmation_description')}
                </S.EmailWarningText>
                <S.EmailWarningButton
                  type="button"
                  onClick={openEmailConfirmation}
                >
                  {t('pages.cabinet.confirm_email')}
                </S.EmailWarningButton>
              </S.EmailWarning>
            )}
          </S.EmailInputContainer>

          <Input
            appearance="filled"
            disabled
            label={t('pages.cabinet.mobile_phone')}
            onChange={noop}
            value={phone}
            withEllipsis
          />
        </S.Fields>
      </S.ProfileCard>

      <S.InfoCard>
        <S.Title $variant="2">{t('pages.cabinet.my_data')}</S.Title>
        <S.InfoRows>
          <S.InfoRow>
            <S.InfoLabel>{t('pages.cabinet.profile_number')}</S.InfoLabel>
            <S.InfoValue>{user.id}</S.InfoValue>
          </S.InfoRow>
          <S.InfoRow>
            <S.InfoLabel>{t('pages.cabinet.registration_date')}</S.InfoLabel>
            <S.InfoValue>{registrationDate}</S.InfoValue>
          </S.InfoRow>
        </S.InfoRows>
      </S.InfoCard>

      {isRemoveAccountVisible && (
        <S.RemoveCard>
          <S.RemoveContent>
            <S.Title $variant="2">{t('pages.cabinet.remove_account')}</S.Title>
            <S.RemoveDescription>
              {t('pages.cabinet.remove_account_description')}
            </S.RemoveDescription>
          </S.RemoveContent>
          <S.RemoveButton
            type="button"
            onPointerDown={event => event.preventDefault()}
            onClick={openRemoveAccount}
          >
            {t('common.remove')}
          </S.RemoveButton>
        </S.RemoveCard>
      )}

      {user.email && (
        <EmailConfirmationModal
          email={user.email}
          isOpen={isEmailConfirmationOpen}
          onClose={closeEmailConfirmation}
        />
      )}

      <RemoveAccountModalSheet
        isOpen={isRemoveAccountOpen}
        onClose={closeRemoveAccount}
        onSubmit={openRemoveAccountConfirm}
      />

      <WarningModal
        isOpen={isRemoveAccountConfirmOpen}
        onClose={closeRemoveAccountConfirm}
        title={t('pages.cabinet.remove_account_question_mark')}
        description={t('pages.cabinet.cant_restore_it_after_removing')}
        actions={
          <>
            <Button
              appearance="redOutlined"
              isFullWidth
              borderRadius={14}
              isLoading={isDestroyingUser}
              disabled={isDestroyingUser}
              onClick={removeAccount}
            >
              {t('common.remove')}
            </Button>
            <Button
              appearance="secondary"
              isFullWidth
              borderRadius={14}
              disabled={isDestroyingUser}
              onClick={closeRemoveAccountConfirm}
            >
              {t('common.decline')}
            </Button>
          </>
        }
      />

      <WarningModal
        isOpen={isRemoveAccountErrorOpen}
        onClose={closeRemoveAccountError}
        title={t('pages.cabinet.cant_remove_account')}
        description={destroyAccountError}
      />
    </S.Container>
  );
};
